import { useCallback, useEffect, useRef } from 'react'
import { useTimer } from 'react-timer-hook'
import { POMODORO_STATUS } from '@constants'
import { usePomodoro } from '@hooks'
import { useAlertStore, usePomodoroStore } from '@stores'
import {
  formatPluralLabel,
  requestPomodoroNotificationPermission,
  sendPomodoroNotification,
} from '@utils'

const INITIAL_POMODORO_SETTINGS = {
  focus: 1,
  shortBreak: 0,
  longBreak: 0,
  allPomodoro: false,
}

const pomodoroTimerControls = {
  handleStart: () => {},
  handlePause: () => {},
  handleRestart: () => {},
}

const getPomodoroTimerControls = () => pomodoroTimerControls

export const usePomodoroTimer = () => {
  const {
    activePomodoro,
    selectedConfig,
    status,
    pomodoroSettings,
    setStatus,
    setActivePomodoro,
    setPomodoroSettings,
    pomodoroId,
    setPomodoroId,
    setSelectedConfig,
    timerState,
    setTimerState,
  } = usePomodoroStore()

  const { startPomodoro, finishPomodoro, addFocusSecondsToPomodoro } =
    usePomodoro()
  const { setAlert } = useAlertStore.getState()
  const syncedExpiryRef = useRef(null)
  const initialSyncKeyRef = useRef(null)

  const TIMER_STORAGE_KEY = 'to_focus_pomodoro_timer_v1'

  const persistTimer = (overrides = {}) => {
    try {
      const state = usePomodoroStore.getState()
      const data = {
        status: state.status,
        timerState: state.timerState,
        activePomodoro: state.activePomodoro,
        pomodoroId: state.pomodoroId,
        pomodoroSettings: state.pomodoroSettings,
        selectedConfig: state.selectedConfig,
        ...overrides,
      }

      if (
        (!data.pomodoroId || data.pomodoroId === null) &&
        data.status === POMODORO_STATUS.PROGRESS
      ) {
        try {
          const result = startPomodoro(data.selectedConfig?.id)
          if (result && result.id) {
            setPomodoroId(result.id)
            data.pomodoroId = result.id
          }
        } catch (e) {
          // ignore create session errors
        }
      }

      localStorage.setItem(TIMER_STORAGE_KEY, JSON.stringify(data))
    } catch (e) {
      // ignore storage errors
    }
  }

  const getFocusSegmentSeconds = ({
    activeFocus = activePomodoro,
    timer = timerState,
    now = Date.now(),
  } = {}) => {
    if (activeFocus?.title !== 'FOCUS' || !timer?.focusStartedAt) {
      return 0
    }

    const startedAtMs = new Date(timer.focusStartedAt).getTime()
    if (!Number.isFinite(startedAtMs)) {
      return 0
    }

    const elapsedSeconds = Math.max(0, Math.floor((now - startedAtMs) / 1000))

    if (!timer.expiryTimestamp) {
      return elapsedSeconds
    }

    const expiryMs = new Date(timer.expiryTimestamp).getTime()
    if (!Number.isFinite(expiryMs)) {
      return elapsedSeconds
    }

    const segmentSeconds = Math.max(
      0,
      Math.floor((expiryMs - startedAtMs) / 1000)
    )
    return Math.min(elapsedSeconds, segmentSeconds)
  }

  const registerFocusSeconds = (secondsToAdd, sessionId = pomodoroId) => {
    const safeSeconds = Math.max(0, Math.floor(Number(secondsToAdd) || 0))
    if (!sessionId || safeSeconds === 0) {
      return
    }

    addFocusSecondsToPomodoro(sessionId, safeSeconds)
  }

  const getFocusStartedAtForPhase = phase =>
    phase?.title === 'FOCUS' ? new Date().toISOString() : null

  const resetInitialState = () => {
    const focusTime = selectedConfig?.focusTime ?? 25

    setStatus(POMODORO_STATUS.INITIAL)
    setActivePomodoro({ title: 'FOCUS', time: focusTime })
    setPomodoroSettings({ ...INITIAL_POMODORO_SETTINGS })
  }

  const getNextPomodoroPhase = (settings, config) => {
    if (
      settings.focus === 4 &&
      settings.shortBreak === 3 &&
      settings.longBreak === 0
    ) {
      return {
        nextPomodoro: {
          title: 'LONG_BREAK',
          time: config.longBreakTime,
        },
        updatedSettings: {
          ...settings,
          allPomodoro: true,
        },
      }
    }

    if (settings.focus > settings.shortBreak) {
      return {
        nextPomodoro: {
          title: 'SHORT_BREAK',
          time: config.shortBreakTime,
        },
        updatedSettings: {
          ...settings,
          shortBreak: settings.shortBreak + 1,
        },
      }
    }

    return {
      nextPomodoro: {
        title: 'FOCUS',
        time: config.focusTime,
      },
      updatedSettings: {
        ...settings,
        focus: settings.focus + 1,
      },
    }
  }

  const finishPomodoroFlow = () => {
    finishPomodoro(pomodoroId)
    setAlert(true)
    localStorage.removeItem(TIMER_STORAGE_KEY)
    sendPomodoroNotification({
      title: 'Pomodoro concluído',
      body: 'Intervalo longo finalizado. Vamos focar de novo?',
    })
  }

  const onPomodoroEnd = () => {
    if (activePomodoro?.title === 'FOCUS') {
      registerFocusSeconds(
        getFocusSegmentSeconds({
          timer: usePomodoroStore.getState().timerState,
        }) || activePomodoro.time * 60
      )
    }

    if (pomodoroSettings.allPomodoro) {
      resetInitialState()
      return finishPomodoroFlow()
    }

    const { nextPomodoro, updatedSettings } = getNextPomodoroPhase(
      pomodoroSettings,
      selectedConfig
    )

    const newExpiry = new Date()
    newExpiry.setSeconds(newExpiry.getSeconds() + nextPomodoro.time * 60)

    if (nextPomodoro.title === 'SHORT_BREAK') {
      sendPomodoroNotification({
        title: 'Hora do intervalo curto',
        body: `Faça uma pausa rápida de ${formatPluralLabel(nextPomodoro.time, 'minuto', 'minutos')}.`,
      })
    }

    if (nextPomodoro.title === 'FOCUS') {
      sendPomodoroNotification({
        title: 'Hora de focar',
        body: `Volte ao foco por ${formatPluralLabel(nextPomodoro.time, 'minuto', 'minutos')}.`,
      })
    }

    if (nextPomodoro.title === 'LONG_BREAK') {
      sendPomodoroNotification({
        title: 'Intervalo longo liberado',
        body: `Bom trabalho. Aproveite ${formatPluralLabel(nextPomodoro.time, 'minuto', 'minutos')} de descanso.`,
      })
    }

    setPomodoroSettings(updatedSettings)
    setActivePomodoro(nextPomodoro)
    setTimerState(prev => ({
      ...prev,
      expiryTimestamp: newExpiry.toISOString(),
      remainingSeconds: null,
      focusStartedAt: getFocusStartedAtForPhase(nextPomodoro),
    }))
    setStatus(POMODORO_STATUS.PROGRESS)

    persistTimer({
      status: POMODORO_STATUS.PROGRESS,
      activePomodoro: nextPomodoro,
      pomodoroSettings: updatedSettings,
      timerState: {
        expiryTimestamp: newExpiry.toISOString(),
        remainingSeconds: null,
        focusStartedAt: getFocusStartedAtForPhase(nextPomodoro),
      },
    })
  }

  const getInitialExpiryTimestamp = () => {
    if (status === POMODORO_STATUS.PROGRESS && timerState.expiryTimestamp) {
      return new Date(timerState.expiryTimestamp)
    }

    if (
      status === POMODORO_STATUS.PAUSE &&
      timerState.remainingSeconds != null
    ) {
      const date = new Date()
      date.setSeconds(date.getSeconds() + timerState.remainingSeconds)
      return date
    }

    return new Date(Date.now() + (activePomodoro?.time || 25) * 60 * 1000)
  }

  const initialExpiryTimestamp = getInitialExpiryTimestamp()

  const { hours, seconds, minutes, pause, restart } = useTimer({
    autoStart: false,
    expiryTimestamp: initialExpiryTimestamp,
    onExpire: () => {
      console.log('Pomodoro expired, finishing flow onExpire')
      onPomodoroEnd()
    },
  })

  useEffect(() => {
    if (activePomodoro) {
      const totalSeconds = hours * 3600 + minutes * 60 + seconds
      const currentProgress = Math.abs(
        (100 * totalSeconds) / (activePomodoro.time * 60) - 100
      )
      usePomodoroStore.setState({
        progressValue: currentProgress,
        timerState: {
          ...usePomodoroStore.getState().timerState,
          hours,
          minutes,
          seconds,
        },
      })
    }
  }, [activePomodoro, seconds, hours, minutes])

  useEffect(() => {
    if (!timerState.expiryTimestamp) {
      syncedExpiryRef.current = null
      return
    }

    const nextExpiry = timerState.expiryTimestamp

    if (status === POMODORO_STATUS.PROGRESS) {
      if (syncedExpiryRef.current !== nextExpiry) {
        syncedExpiryRef.current = nextExpiry
        restart(new Date(nextExpiry), true)
      }
      return
    }

    if (status === POMODORO_STATUS.INITIAL) {
      syncedExpiryRef.current = nextExpiry
      restart(new Date(nextExpiry), false)
    }
  }, [restart, status, timerState.expiryTimestamp])

  useEffect(() => {
    if (status !== POMODORO_STATUS.INITIAL || !activePomodoro?.time) {
      initialSyncKeyRef.current = null
      return
    }

    const targetSeconds = activePomodoro.time * 60
    const syncKey = `${selectedConfig?.id ?? 'default'}:${activePomodoro.title}:${targetSeconds}`

    if (
      initialSyncKeyRef.current === syncKey &&
      timerState.expiryTimestamp === null &&
      timerState.remainingSeconds === targetSeconds
    ) {
      return
    }

    initialSyncKeyRef.current = syncKey

    const resetDate = new Date()
    resetDate.setSeconds(resetDate.getSeconds() + targetSeconds)

    syncedExpiryRef.current = null
    restart(resetDate, false)

    if (
      timerState.expiryTimestamp !== null ||
      timerState.remainingSeconds !== targetSeconds
    ) {
      setTimerState(prev => ({
        ...prev,
        expiryTimestamp: null,
        remainingSeconds: targetSeconds,
        focusStartedAt: null,
      }))
    }
  }, [
    activePomodoro?.time,
    activePomodoro?.title,
    restart,
    selectedConfig?.id,
    setTimerState,
    status,
    timerState.expiryTimestamp,
    timerState.remainingSeconds,
  ])

  useEffect(() => {
    try {
      const raw = localStorage.getItem(TIMER_STORAGE_KEY)
      if (!raw) return

      const data = JSON.parse(raw)
      if (!data) return

      if (data.activePomodoro) setActivePomodoro(data.activePomodoro)
      if (data.pomodoroId) setPomodoroId(data.pomodoroId)
      if (data.selectedConfig) setSelectedConfig(data.selectedConfig)
      if (data.pomodoroSettings) setPomodoroSettings(data.pomodoroSettings)

      if (
        data.status === POMODORO_STATUS.PROGRESS &&
        data.timerState?.expiryTimestamp
      ) {
        let workingSettings =
          data.pomodoroSettings ?? { ...INITIAL_POMODORO_SETTINGS }
        let workingPomodoro =
          data.activePomodoro ?? {
            title: 'FOCUS',
            time: data.selectedConfig?.focusTime ?? 25,
          }
        let expiry = new Date(data.timerState.expiryTimestamp)
        let focusStartedAt = data.timerState?.focusStartedAt ?? null
        const now = Date.now()

        while (expiry.getTime() <= now) {
          if (workingPomodoro.title === 'FOCUS') {
            const startedAtMs = focusStartedAt
              ? new Date(focusStartedAt).getTime()
              : expiry.getTime() - workingPomodoro.time * 60 * 1000
            const focusSeconds = Number.isFinite(startedAtMs)
              ? Math.max(
                  0,
                  Math.floor((expiry.getTime() - startedAtMs) / 1000)
                )
              : workingPomodoro.time * 60
            registerFocusSeconds(
              Math.min(focusSeconds, workingPomodoro.time * 60),
              data.pomodoroId
            )
          }

          if (workingSettings.allPomodoro) {
            resetInitialState()
            finishPomodoroFlow()
            return
          }

          const { nextPomodoro, updatedSettings } = getNextPomodoroPhase(
            workingSettings,
            data.selectedConfig
          )

          workingSettings = updatedSettings
          workingPomodoro = nextPomodoro
          focusStartedAt =
            nextPomodoro.title === 'FOCUS' ? expiry.toISOString() : null
          expiry = new Date(expiry.getTime() + nextPomodoro.time * 60 * 1000)
        }

        if (expiry > new Date()) {
          setPomodoroSettings(workingSettings)
          setActivePomodoro(workingPomodoro)
          restart(expiry, true)
          setTimerState(prev => ({
            ...prev,
            expiryTimestamp: expiry.toISOString(),
            remainingSeconds: null,
            focusStartedAt,
          }))
          setStatus(POMODORO_STATUS.PROGRESS)
          persistTimer({
            status: POMODORO_STATUS.PROGRESS,
            activePomodoro: workingPomodoro,
            pomodoroSettings: workingSettings,
            timerState: {
              expiryTimestamp: expiry.toISOString(),
              remainingSeconds: null,
              focusStartedAt,
            },
          })
          return
        }

        console.log('Pomodoro expired while away, finishing flow')
        onPomodoroEnd()
        return
      }

      if (
        data.status === POMODORO_STATUS.PAUSE &&
        data.timerState?.remainingSeconds != null
      ) {
        const date = new Date()
        date.setSeconds(date.getSeconds() + data.timerState.remainingSeconds)
        restart(date, false)
        setTimerState(prev => ({
          ...prev,
          expiryTimestamp: date.toISOString(),
          remainingSeconds: data.timerState.remainingSeconds,
          focusStartedAt: null,
        }))
        setStatus(POMODORO_STATUS.PAUSE)
      }
    } catch (e) {
      // ignore
    }
  }, [])

  const handleStart = useCallback(() => {
    let expiryToPersist = null
    let focusStartedAtToPersist = null

    requestPomodoroNotificationPermission()

    if (status === POMODORO_STATUS.PAUSE) {
      const remainingSeconds =
        timerState.remainingSeconds ?? hours * 3600 + minutes * 60 + seconds
      const resumeTime = new Date()
      resumeTime.setSeconds(resumeTime.getSeconds() + remainingSeconds)
      focusStartedAtToPersist = getFocusStartedAtForPhase(activePomodoro)
      restart(resumeTime, true)
      setTimerState(prev => ({
        ...prev,
        expiryTimestamp: resumeTime.toISOString(),
        remainingSeconds: null,
        focusStartedAt: focusStartedAtToPersist,
      }))
      expiryToPersist = resumeTime
    } else {
      const result = startPomodoro(selectedConfig?.id)
      if (result) {
        setPomodoroId(result.id)
      }
      const newExpiryTimestamp = new Date(
        Date.now() + (activePomodoro?.time || 25) * 60 * 1000
      )
      focusStartedAtToPersist = getFocusStartedAtForPhase(activePomodoro)
      setTimerState(prev => ({
        ...prev,
        expiryTimestamp: newExpiryTimestamp.toISOString(),
        remainingSeconds: null,
        focusStartedAt: focusStartedAtToPersist,
      }))
      restart(newExpiryTimestamp, true)
      expiryToPersist = newExpiryTimestamp
    }

    setStatus(POMODORO_STATUS.PROGRESS)
    try {
      if (expiryToPersist) {
        persistTimer({
          status: POMODORO_STATUS.PROGRESS,
          timerState: {
            expiryTimestamp: expiryToPersist.toISOString(),
            remainingSeconds: null,
            focusStartedAt: focusStartedAtToPersist,
          },
        })
      }
    } catch (e) {
      // ignore
    }
  }, [
    activePomodoro?.time,
    activePomodoro?.title,
    hours,
    minutes,
    pomodoroId,
    seconds,
    selectedConfig?.id,
    status,
    timerState.remainingSeconds,
  ])

  const handlePause = useCallback(() => {
    if (
      activePomodoro?.title === 'FOCUS' &&
      status === POMODORO_STATUS.PROGRESS
    ) {
      registerFocusSeconds(
        getFocusSegmentSeconds({
          timer: usePomodoroStore.getState().timerState,
        })
      )
    }

    pause()
    const remainingSeconds = hours * 3600 + minutes * 60 + seconds
    setTimerState(prev => ({
      ...prev,
      expiryTimestamp: null,
      remainingSeconds,
      focusStartedAt: null,
    }))
    setStatus(POMODORO_STATUS.PAUSE)
    try {
      persistTimer({
        status: POMODORO_STATUS.PAUSE,
        timerState: {
          ...usePomodoroStore.getState().timerState,
          remainingSeconds,
          focusStartedAt: null,
        },
      })
    } catch (e) {
      // ignore
    }
  }, [
    activePomodoro?.title,
    hours,
    minutes,
    pause,
    seconds,
    setTimerState,
    status,
  ])

  const handleRestart = useCallback(() => {
    if (
      activePomodoro?.title === 'FOCUS' &&
      status === POMODORO_STATUS.PROGRESS
    ) {
      registerFocusSeconds(
        getFocusSegmentSeconds({
          timer: usePomodoroStore.getState().timerState,
        })
      )
    }

    const focusTime = selectedConfig?.focusTime ?? activePomodoro?.time ?? 25
    const resetSeconds = focusTime * 60

    pause()
    resetInitialState()
    const newDate = new Date()
    newDate.setSeconds(newDate.getSeconds() + focusTime * 60)
    syncedExpiryRef.current = null
    restart(newDate, false)
    setTimerState(prev => ({
      ...prev,
      expiryTimestamp: null,
      remainingSeconds: resetSeconds,
      focusStartedAt: null,
    }))
    try {
      persistTimer({
        status: POMODORO_STATUS.INITIAL,
        activePomodoro: { title: 'FOCUS', time: focusTime },
        pomodoroSettings: { ...INITIAL_POMODORO_SETTINGS },
        timerState: {
          ...usePomodoroStore.getState().timerState,
          expiryTimestamp: null,
          remainingSeconds: resetSeconds,
          focusStartedAt: null,
        },
      })
    } catch (e) {
      // ignore
    }
  }, [
    activePomodoro?.time,
    activePomodoro?.title,
    pause,
    selectedConfig?.focusTime,
    setTimerState,
    status,
  ])

  pomodoroTimerControls.handleStart = handleStart
  pomodoroTimerControls.handlePause = handlePause
  pomodoroTimerControls.handleRestart = handleRestart

  return {
    hours,
    minutes,
    seconds,
    handleStart,
    handlePause,
    handleRestart,
  }
}

export { getPomodoroTimerControls }
